import { useEffect, useRef, useState } from 'react'

import { DEFAULT_LINE_MATERIAL } from '@editor/pages/projects/editor/objects/Polyline/constants'
import { RootState } from '@editor/store/app'
import { useThree } from '@react-three/fiber'
import { useSelector } from 'react-redux'
import { BufferAttribute, BufferGeometry, Mesh, ShapeUtils } from 'three'
import { Line2, LineGeometry } from 'three-stdlib'

import { HoverContainerProps } from '../definitions'
import useHoverContainerEventHandlers from '../hooks/useHoverContainerEventHandlers'

const HoverContainer = (props: HoverContainerProps) => {
  const { id, points, editable } = props
  const { camera } = useThree()

  // Store
  const hoveredElementId = useSelector((state: RootState) => state.toolUtil.core.hoveredElementId)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)

  // Refs
  const meshRef = useRef<Mesh>(null)

  // Derived state
  const isHovered = hoveredElementId === id
  const isSelected = selectedElementIds.includes(id)
  const isHoverSelected = isHovered || isSelected

  // Hooks
  const { handleMouseEnter, handleMouseLeave, handleMouseDown, handleClick } = useHoverContainerEventHandlers(props)

  const [meshGeometry] = useState(() => {
    if (!points?.length) return null

    const buffer = new BufferGeometry()
    const positions = new Float32Array(points.length * 3)
    points.forEach((vertex, index) => {
      vertex.toArray(positions, index * 3)
    })

    const indi = ShapeUtils.triangulateShape(points, [])

    buffer.setAttribute('position', new BufferAttribute(positions, 3))
    buffer.setIndex(indi.flat())
    buffer.computeVertexNormals()
    buffer.computeBoundingBox()
    buffer.computeBoundingSphere()
    return buffer
  })

  const [outline] = useState(() => {
    const geometry = new LineGeometry()

    geometry.setPositions([...points, points[0]].flatMap((p) => p.toArray()))

    const outlineMat = DEFAULT_LINE_MATERIAL.clone()

    outlineMat.dashed = false
    outlineMat.color.set('#0bc5ea') // Use white when set as transparent since 'transparent' is not a valid color
    outlineMat.linewidth = 0.002

    const outlineLn = new Line2(geometry, outlineMat)
    outlineLn.computeLineDistances()

    return outlineLn
  })

  /**
   * Update mesh and outline on points change
   */
  useEffect(() => {
    if (!meshGeometry || !outline || points.length < 2) return

    meshGeometry.setFromPoints(points)
    meshGeometry.computeBoundingBox()
    meshGeometry.computeBoundingSphere()

    const indi = ShapeUtils.triangulateShape(
      points.map((p) => p.clone().project(camera)),
      [],
    )
    meshGeometry.setIndex(indi.flat())
    meshGeometry.computeVertexNormals()

    meshRef.current?.updateMatrixWorld()

    outline.geometry.setPositions([...points, points[0]].flatMap((p) => p.toArray()))
    outline.geometry.computeVertexNormals()
  }, [points, meshGeometry, outline, camera])

  return (
    <group>
      {meshGeometry && (
        <mesh
          ref={meshRef}
          geometry={meshGeometry}
          onPointerEnter={editable ? handleMouseEnter : undefined}
          onPointerLeave={editable ? handleMouseLeave : undefined}
          onPointerDown={editable ? handleMouseDown : undefined}
          onClick={editable ? handleClick : undefined}
          renderOrder={-1}
        >
          <meshBasicMaterial color="blue" transparent opacity={0} depthTest={false} depthWrite={false} />
        </mesh>
      )}

      <primitive object={outline} visible={isHoverSelected} renderOrder={10} />
    </group>
  )
}

export default HoverContainer
